import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ContractData } from "@/pages/contracts/types";
import {
  ContractUpdatePayload,
  useDeleteContract,
  useUpdateContract,
  useUpdateDealCurrency
} from "@/pages/contracts/hooks";
import type { ContractFieldUpdate } from "@/pages/contracts/ContractDetailedView/ContractDetailedView";
import {
  coerceContractFieldValue,
  coerceContractFields
} from "@/pages/contracts/contractFieldValue";
import { editRestrictionMessageKey } from "@/services/hooks/entities/editRestrictions";

export const useContractActions = (contractEntity: ContractData | undefined) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const handleCopyLink = useCopyLink();

  const [contract, setContract] = useState<ContractData | undefined>(
    contractEntity
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (contractEntity) setContract(contractEntity);
  }, [contractEntity]);

  const updateContract = useUpdateContract(contract?.id);
  const updateDealCurrency = useUpdateDealCurrency(contract?.id);
  const deleteContract = useDeleteContract();

  const onUpdateField: ContractFieldUpdate = useCallback(
    (field, newValue, additionalFields, options) => {
      if (!contract) return;
      const propagatesCurrencyToDeal =
        field === "currency" &&
        !!contract.dealId &&
        !contract.isDetachedFromDeal &&
        typeof newValue === "string";
      if (propagatesCurrencyToDeal) {
        updateDealCurrency.mutate({
          pathParams: { dealId: contract.dealId },
          data: { currency: newValue }
        });
      }
      const finalValue = coerceContractFieldValue(field, newValue, options);
      const normalizedAdditionalFields = coerceContractFields(
        additionalFields,
        options
      );
      const updated = {
        ...contract,
        [field]: finalValue,
        ...normalizedAdditionalFields
      };
      const previous = contract;
      setContract(updated);

      if (field === "onboardingStageId") return;

      // dealId is a financial field on the server: including it on every PUT
      // makes non-financial edits fail the financials-permission check on a
      // locked contract. Send it only where it does work — the currency flow
      // that propagates the new currency to the linked deal.
      const changedFields: Partial<ContractUpdatePayload> = {
        ...(propagatesCurrencyToDeal ? { dealId: contract.dealId } : {}),
        [field]: finalValue,
        ...normalizedAdditionalFields
      };

      updateContract.mutate(
        {
          pathParams: { contractId: contract.id },
          data: changedFields
        },
        {
          onSuccess: (response) => {
            setContract(response);
            if (field === "currency" && response.dealId) {
              queryClient.invalidateQueries({
                queryKey: [
                  "deals",
                  { pathParams: { dealsIds: response.dealId } }
                ]
              });
            }
          },
          onError: (error) => {
            setContract(previous);
            toast.error(
              t(
                editRestrictionMessageKey(error) ??
                  "contracts.detailedView.updateError"
              )
            );
          }
        }
      );
    },
    [contract, updateContract, updateDealCurrency, t, queryClient]
  );

  const handleDelete = (closeDrawer?: () => void) => {
    if (!contract) return;
    deleteContract.mutate(
      { pathParams: { contractId: contract.id } },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          closeDrawer?.();
          queryClient.invalidateQueries({ queryKey: ["contracts/filter"] });
        },
        onError: () => {
          toast.error(t("contracts.detailedView.deleteError"));
        }
      }
    );
  };

  const getTopOptions = (): DetailedViewOptions => ({});

  const getOptions = (): DetailedViewOptions =>
    ({
      copyLink: () => handleCopyLink(true, contract?.id),
      ...(contract?.id && {
        contractLog: () =>
          navigate(`/contracts/${contract.id}/log`, { replace: true })
      }),
      delete: {
        action: () => setIsDeleteModalOpen(true),
        variant: "destructive"
      }
    }) as DetailedViewOptions;

  const renderModals = (isDropdownClosing = false, onClose?: () => void) => (
    <DeleteEntityModal
      isOpen={isDeleteModalOpen && !isDropdownClosing}
      setIsOpen={setIsDeleteModalOpen}
      entity={EntityType.CONTRACT}
      entityName={contract?.name || ""}
      handleDelete={() => handleDelete(onClose)}
    />
  );

  return {
    contract,
    onUpdateField,
    getTopOptions,
    getOptions,
    renderModals
  };
};
